// Collections on a client's report — two tools the bureau dispute doesn't cover:
//  1. a debt validation letter mailed to the collection agency itself, and
//  2. a CFPB complaint filed directly against that agency ("Debt collection").
// The complaint's yes/no answers are filled from what's actually on record
// (was a letter mailed? did they answer?) — never assumed — and a person
// always presses Submit at the CFPB.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "./Modal";
import {
  ApiError, collections as collectionsApi, collectorWarningText,
  type CollectionAccount, type CollectorComplaintDraft,
} from "../lib/api";

const BUREAU: Record<string, string> = { equifax: "Equifax", experian: "Experian", transunion: "TransUnion" };
const money = (c: number | null) => (c == null ? null : (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }));
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);
const yesNo = (v: boolean | null) => (v == null ? "Not asked" : v ? "Yes" : "No");

// The CFPB's own wording for the debt-collection issues we allow. The default
// is the one that fits a validation dispute; staff pick another only when it
// is actually true for this client.
const ISSUES: Array<{ issue: string; subs: string[] }> = [
  { issue: "Written notification about debt", subs: ["Didn't receive enough information to verify debt", "Didn't receive notice of right to dispute", "Notification didn't disclose it was an attempt to collect a debt"] },
  { issue: "Attempted to collect debt not owed", subs: ["Debt is not yours", "Debt was paid", "Debt was already discharged in bankruptcy and is no longer owed", "Debt was result of identity theft"] },
  { issue: "False statements or representation", subs: ["Attempted to collect wrong amount", "Impersonated attorney, law enforcement, or government official", "Indicated you were committing crime by not paying debt", "Indicated shouldn't respond to lawsuit"] },
];

export default function CollectionAccountsPanel({ clientId, refreshKey }: { clientId: string; refreshKey?: string | number }) {
  const [accounts, setAccounts] = useState<CollectionAccount[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [letterFor, setLetterFor] = useState<CollectionAccount | null>(null);
  const [complaintFor, setComplaintFor] = useState<CollectionAccount | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try { setAccounts((await collectionsApi.list(clientId)).accounts); }
    catch (e: any) { setErr(e?.body?.message || e?.message || "Couldn't load collection accounts."); }
  }, [clientId]);
  useEffect(() => { setAccounts(null); load(); }, [load, refreshKey]);

  if (err && !accounts) return <section className="sub-panel" style={{ marginBottom: 14 }}><p className="error-text" role="alert">{err}</p><button className="btn-secondary" onClick={load}>Try again</button></section>;
  if (!accounts || accounts.length === 0) return null; // nothing to show when the report has no collections

  return (
    <section className="sub-panel" aria-labelledby="collections-title" data-testid="collection-accounts" style={{ marginBottom: 14 }}>
      <h3 id="collections-title" style={{ margin: "0 0 4px" }}>Collection accounts</h3>
      <p className="muted small" style={{ margin: "0 0 10px" }}>
        Go straight to the collection agency: mail a debt validation letter, then file a CFPB complaint against the agency if they don't answer. Results vary — nothing here guarantees a deletion.
      </p>
      {notice && <p className="small" role="status" style={{ margin: "0 0 8px", color: "var(--ne-success-text)" }}>{notice}</p>}
      {err && <p className="error-text" role="alert" style={{ margin: "0 0 8px" }}>{err}</p>}
      <div style={{ display: "grid", gap: 8 }}>
        {accounts.map((a) => (
          <div key={a.finding_id} style={{ border: "1px solid var(--ne-line)", borderRadius: 8, padding: "10px 12px", background: "var(--ne-surface-2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div>
                <strong>{a.creditor_name}</strong>
                {a.account_number_as_reported && <span className="muted small"> · {a.account_number_as_reported}</span>}
                <div className="muted small">
                  {[a.original_creditor ? `Original creditor: ${a.original_creditor}` : null, money(a.balance_cents), a.bureaus.map((b) => BUREAU[b] || b).join(", ")].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start" }}>
                {!a.validation_letter && (
                  <button type="button" className="btn-secondary" onClick={() => { setNotice(null); setLetterFor(a); }} data-testid="draft-validation-letter">Draft validation letter</button>
                )}
                {!a.cfpb_filing && (
                  <button type="button" className="btn-secondary" onClick={() => { setNotice(null); setComplaintFor(a); }} data-testid="prepare-collector-complaint">Prepare CFPB complaint</button>
                )}
              </div>
            </div>
            <div className="small" style={{ marginTop: 6, display: "flex", gap: 14, flexWrap: "wrap" }}>
              <span><strong>Validation letter:</strong> {a.validation_letter ? (a.validation_letter.mailed_at ? `mailed ${day(a.validation_letter.mailed_at)}` : `${a.validation_letter.status} — not mailed yet`) : "not drafted"}</span>
              {a.validation_letter?.mailed_at && <span><strong>Collector answered:</strong> {a.response_logged ? "yes" : "nothing logged"}</span>}
              <span><strong>CFPB complaint:</strong> {a.cfpb_filing ? (a.cfpb_filing.submitted_at ? `submitted ${day(a.cfpb_filing.submitted_at)}` : <Link to="/compliance">{a.cfpb_filing.draft_status} — open in Compliance</Link>) : "not prepared"}</span>
            </div>
          </div>
        ))}
      </div>
      {letterFor && (
        <ValidationLetterModal clientId={clientId} account={letterFor} onClose={() => setLetterFor(null)}
          onSaved={(m) => { setLetterFor(null); setNotice(m); load(); }} />
      )}
      {complaintFor && (
        <ComplaintModal clientId={clientId} account={complaintFor} onClose={() => { setComplaintFor(null); load(); }} />
      )}
    </section>
  );
}

function ValidationLetterModal({ clientId, account, onClose, onSaved }: {
  clientId: string; account: CollectionAccount; onClose: () => void; onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(account.creditor_name);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function save() {
    setErr(null);
    if (address.trim().split("\n").filter((l) => l.trim()).length < 2) return setErr("Enter the collector's mailing address (street on one line, city/state/ZIP on the next). It's on the credit report or the collector's letter.");
    setBusy(true);
    try {
      const r = await collectionsApi.draftValidationLetter(clientId, account.finding_id, { collector_name: name.trim() || undefined, collector_address: address.trim() });
      onSaved(`Validation letter drafted in Round ${r.round.round_number}. Review and mail it from the Disputes page.`);
    } catch (e: any) {
      setErr(e instanceof ApiError && e.status === 409 ? "A validation letter for this account already exists." : e?.body?.message || e?.message || "Couldn't draft the letter.");
      setBusy(false);
    }
  }
  return (
    <Modal open title="Debt validation letter" onClose={onClose}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="primary-button" onClick={save} disabled={busy}>{busy ? "Drafting…" : "Draft letter"}</button>
      </>}>
      <div className="stacked-form">
        <p className="muted small" style={{ margin: 0 }}>
          Asks the collector to prove the debt: the amount and how it was calculated, the original creditor, and proof they own it — and to mark the account as disputed. It's a draft; you review and mail it like any other letter.
        </p>
        <label className="form-field"><span>Collection agency</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} /></label>
        <label className="form-field"><span>Collector's mailing address</span>
          <textarea rows={3} value={address} onChange={(e) => setAddress(e.target.value)} placeholder={"PO Box 1234\nGreenville, SC 29603"} /></label>
        {err && <p className="error-text" role="alert" style={{ margin: 0 }}>{err}</p>}
      </div>
    </Modal>
  );
}

function ComplaintModal({ clientId, account, onClose }: { clientId: string; account: CollectionAccount; onClose: () => void }) {
  const [issueIdx, setIssueIdx] = useState(0);
  const [sub, setSub] = useState(ISSUES[0].subs[0]);
  const [draft, setDraft] = useState<CollectorComplaintDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function prepare() {
    setErr(null); setBusy(true);
    try {
      const r = await collectionsApi.prepareComplaint(clientId, account.finding_id, { issue: ISSUES[issueIdx].issue, sub_issue: sub });
      setDraft(r.draft_content);
    } catch (e: any) { setErr(e?.body?.message || e?.message || "Couldn't prepare the complaint."); }
    finally { setBusy(false); }
  }

  return (
    <Modal open title={`CFPB complaint — ${account.creditor_name}`} onClose={onClose} wide
      footer={draft ? (
        <><button type="button" className="btn-secondary" onClick={onClose}>Close</button><Link className="primary-button" to="/compliance" onClick={onClose}>Open in Compliance</Link></>
      ) : (
        <><button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="primary-button" onClick={prepare} disabled={busy}>{busy ? "Preparing…" : "Prepare complaint"}</button></>
      )}>
      {!draft ? (
        <div className="stacked-form">
          <p className="muted small" style={{ margin: 0 }}>
            This stages a complaint against the collection agency under the CFPB's "Debt collection" category. Nothing is sent — a person reviews it and presses Submit at the CFPB.
          </p>
          <label className="form-field"><span>What is the problem? (choose what is actually true)</span>
            <select value={issueIdx} onChange={(e) => { const i = Number(e.target.value); setIssueIdx(i); setSub(ISSUES[i].subs[0]); }}>
              {ISSUES.map((x, i) => <option key={x.issue} value={i}>{x.issue}</option>)}
            </select></label>
          <label className="form-field"><span>More specifically</span>
            <select value={sub} onChange={(e) => setSub(e.target.value)}>
              {ISSUES[issueIdx].subs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></label>
          {!account.validation_letter?.mailed_at && (
            <p className="banner-warning small" style={{ margin: 0 }}>{collectorWarningText("validation_letter_not_sent")}</p>
          )}
          {err && <p className="error-text" role="alert" style={{ margin: 0 }}>{err}</p>}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {draft.blockers.length > 0 && (
            <div className="banner-warning" role="note"><strong>Not ready to file:</strong>
              <ul className="small" style={{ margin: "6px 0 0", paddingLeft: 18 }}>{draft.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul></div>
          )}
          {draft.warnings.map((w) => <p key={w} className="banner-warning small" role="note" style={{ margin: 0 }}>{collectorWarningText(w)}</p>)}
          <div className="identity-grid">
            <div className="field-pair"><span className="field-label">Company</span><span>{draft.company.name_as_reported}</span></div>
            <div className="field-pair"><span className="field-label">Account number (its own field)</span><span>{draft.account_number_as_reported || "—"}</span></div>
            <div className="field-pair"><span className="field-label">Issue</span><span>{draft.wizard.issue} → {draft.wizard.sub_issue}</span></div>
            <div className="field-pair"><span className="field-label">Tried to fix it with the company?</span><span>{yesNo(draft.wizard.tried_to_fix)}</span></div>
            <div className="field-pair"><span className="field-label">Requested information?</span><span>{yesNo(draft.wizard.requested_information)}</span></div>
            <div className="field-pair"><span className="field-label">Did they provide it?</span><span>{yesNo(draft.wizard.company_provided_information)}</span></div>
          </div>
          <p className="muted small" style={{ margin: 0 }}>These answers come from what's on file for this client (letters mailed, replies logged), so the complaint is accurate as written.</p>
          <div><span className="field-label">What happened</span><p className="small" style={{ whiteSpace: "pre-wrap", margin: "4px 0 0" }}>{draft.narrative}</p></div>
          <div><span className="field-label">Fair resolution</span><p className="small" style={{ whiteSpace: "pre-wrap", margin: "4px 0 0" }}>{draft.resolution}</p></div>
          <p className="small" role="status" style={{ margin: 0, color: "var(--ne-success-text)" }}>Complaint staged. Open it in Compliance to review, get the client's sign-off and file.</p>
        </div>
      )}
    </Modal>
  );
}
